import { useEffect } from "react";
import Head from "next/head";
import Image from "next/image";

import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "next/link";

import MainHeader from "../components/MainHeader";
import MainFooter from "../components/MainFooter";
import DemoForm from "../components/forms/Form";
import * as dayjs from "dayjs";
import EventRow from "../components/EventRow";
import Slideshow from "../components/Slideshow";

import { getCookie } from "cookies-next";

import fonts from "../styles/FontInter.module.css";
import mainTheme from "../components/mainTheme";
import styles from "../styles/Home.module.css";

import client from "../components/client";
import ExitModal from "../components/ExitModal";
import FormMini from "../components/forms/FormMini";

export default function TemplatePage({ utm, events }) {
  useEffect(() => {
    dataLayer.push({ event: "optimize.activate" });
  }, []);

  let eventList = [];
  for (let event of events) {
    const now = dayjs();
    const eventDate =
      dayjs(event.event_date).format("MMM D, YYYY") != "Invalid Date" ? dayjs(event.event_date) : dayjs();

    if (event.event_type === "webinar" && eventList.length < 3 && eventDate > now) {
      eventList.push(event);
    }
  }
  if (eventList.length < 3) {
    for (let event of events) {
      if (event.event_type == "ondemand" && eventList.length < 3) {
        eventList.push(event);
      }
    }
  }

  return (
    <ThemeProvider theme={mainTheme}>
      <Head>
        <title>Productivity Solutions for Real Estate Agents - Elm Street Technology</title>
        <meta
          name="description"
          content="You will be able to market your listings across multiple platforms, including IDX websites and social media. Gain access to repeat and referral business models. Stay in touch with your clients as their community and networking expert, long after they've closed on their home."
        />
        <link rel="icon" href="/favicon.ico" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@weareelmstreet" />
        <meta name="twitter:creator" content="@weareelmstreet" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://elmstreet.com/agent-solutions" />
        <meta property="og:title" content="Productivity Solutions for Real Estate Agents - Elm Street Technology" />
        <meta
          property="og:description"
          content="You will be able to market your listings across multiple platforms, including IDX websites and social media. Gain access to repeat and referral business models. Stay in touch with your clients as their community and networking expert, long after they've closed on their home."
        />
        <meta property="og:image" content="https://elmstreet.com/og_images/elmstreetog.jpg" />
      </Head>
      <CssBaseline className={fonts} />
      <MainHeader />

      <ExitModal>
        <div
          style={{
            backgroundImage: "url('orangebg.webp')",
            backgroundPosition: "center",
            backgroundSize: "fixed",
            padding: "30px",
            display: "block",
          }}
        >
          <div style={{ textAlign: "center", paddingBottom: "20px" }}>
            {" "}
            <Typography
              variant="h3"
              component="h2"
              style={{ color: "#ffffff", textAlign: "center", paddingBottom: "10px" }}
            >
              Stay up to date...
            </Typography>
            <Typography variant="h5" component="h2" style={{ color: "#333666", textAlign: "center" }}>
              on the latest &amp; greatest in the industry. Be the first to know about:
            </Typography>
            <br />
            <strong>
              + Upcoming webinars with industry leaders
              <br />
              + The latest in marketing &amp; lead gen strategies
              <br />
              + Newly-published industry guides
              <br />
              + First dibs on educational boot camps &amp; seminars
              <br />
            </strong>
          </div>
          <div style={{ backgroundColor: "#ffffff", padding: "25px" }}>
            <FormMini
              buttonText="Register before you go"
              formName="es2_blog"
              listId="147953"
              utmOverride="exit_intent"
            />
          </div>
        </div>
      </ExitModal>

      <div
        style={{
          backgroundImage: "url('agentbg.jpg')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container maxWidth="lg">
          <Grid container>
            <Grid item md={5} sm={12}>
              <div
                style={{
                  color: "#FFFFFF",
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "row",
                  height: "650px",
                }}
              >
                <div>
                  <Typography variant="h2" component="h1">
                    <span style={{ color: mainTheme.palette.secondary.main }}>
                      Empowering Real Estate Agents Every Step of the Way
                    </span>
                    <br />
                  </Typography>
                  <p>
                    Innovative tools designed to help agents generate leads, build relationships, and grow their
                    business with ease.
                  </p>
                  <Button variant="contained" color="secondary" href="#form">
                    schedule a demo
                  </Button>
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "45px",
            paddingBottom: "30px",
          }}
        >
          <div style={{ marginTop: "35px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Simplifying Success for Agents</span>
                </Typography>
                <p>
                  As a real estate agent, you juggle countless responsibilities. Elm Street Technology is here to make
                  your job easier. From lead generation to client follow-ups, our solutions streamline your workload, so
                  you can focus on what matters most—helping your clients find their dream homes.
                </p>
              </Grid>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/agents1.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street For Agents - Work Smarter Not Harder"
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "15px",
            paddingBottom: "45px",
          }}
        >
          <div style={{ marginTop: "35px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/as2.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street For Agents - Work on Your Business"
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={12}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>A Tool for Every Need</span>
                </Typography>

                <p>
                  Our family of brands is tailored to meet the unique challenges agents face.{" "}
                  <Link href="/idx-broker">IDX Broker</Link> enhances your website with MLS integration,{" "}
                  <Link href="/ixact-contact">IXACT Contact</Link> simplifies CRM and email marketing,{" "}
                  <Link href="/morris-marketing-group">Morris Marketing Group</Link> strengthens client loyalty through
                  personalized direct mail, <Link href="/outbound-engine">OutboundEngine</Link> automates your social
                  media and email campaigns, <Link href="/aiva">Aiva</Link> engages and converts leads 24/7, and{" "}
                  <Link href="/voicepad">VoicePad</Link> keeps you connected with telecom solutions.
                </p>
                <Button variant="contained" color="secondary" href="#form">
                  the solution for agents
                </Button>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingBottom: "15px",
          }}
        >
          <div style={{ marginTop: "15px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Stay Connected, Close More Deals</span>
                </Typography>

                <p>
                  Real estate is all about relationships, and we help you maintain them. Our tools ensure you stay
                  top-of-mind with your sphere of influence, respond to leads quickly, and nurture prospects
                  effectively. Building trust and closing deals has never been easier.
                </p>
              </Grid>

              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/teams2.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street For Agents - Work on Your Business"
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "35px",
          }}
        >
          <div style={{ marginTop: "35px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/teams5.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street For Agents - Easy Onboarding"
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={12}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Built for Your Success</span>
                </Typography>
                <p>
                  We understand the unique demands of being a real estate agent. That’s why our solutions are intuitive,
                  easy to use, and designed to fit seamlessly into your daily routine. Whether you're a seasoned agent
                  or just starting out, Elm Street Technology gives you the tools to succeed.
                </p>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div
        style={{
          backgroundImage: "url('orangebg.webp')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container maxWidth="lg" style={{ paddingTop: "80px", paddingBottom: "85px", marginTop: "50px" }}>
          <div style={{}}>
            <a id="form" />
            <Typography variant="h4" component="h4" style={{ color: "#1d1746", textAlign: "center" }}>
              schedule a demo
            </Typography>
            <div style={{ paddingBottom: "40px", textAlign: "center" }}>
              <strong style={{ color: "#1d1746" }}>
                Connect with Elm Street Technology's Success Team today to explore your path to success.
              </strong>
            </div>
            <div style={{ padding: "15px", backgroundColor: "#ffffff" }}>
              <Container
                maxWidth="lg"
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  marginTop: "65px",
                  marginBottom: "65px",
                }}
              >
                <Grid container>
                  <Grid item md={2} sm={12} xs={12} />
                  <Grid item md={8} sm={12} xs={12}>
                    <DemoForm
                      buttonText="Schedule Demo"
                      successMsg={
                        <div style={{ color: "#d37800" }}>
                          <strong>
                            We're Excited to Connect with You! A member of the Elm Street Technology team will be in
                            touch with you shortly.
                          </strong>
                          <br />
                          <br />
                        </div>
                      }
                      listId="147997"
                      formName="es2_agentSolutions"
                      fieldList={["first_name", "last_name", "email", "phone", "roll", "solutions", "currentClient"]}
                    />
                  </Grid>
                </Grid>
              </Container>
            </div>
          </div>
        </Container>
      </div>

      <MainFooter />
    </ThemeProvider>
  );
}

export async function getServerSideProps({ req, res }) {
  let cookies = getCookie("elmlead", { req, res });

  const eventQuery = `
  *[_type == "event" &&  !("Training" in event_category[]->title)] | order(event_date asc) {
    _id,
    title,
    event_date,
    event_type,
    event_category[]->{
      title
    },
    duration,
    description,
    thumbnail,
    reg_url,
    tags[]->{
      tag
    }
  }
  `;

  const events = await client.fetch(eventQuery);

  if (cookies) {
    return { props: { utm: cookies, events } };
  }
  return { props: { events } };
}
